import React, { useMemo } from 'react'
import NumberFormat from 'react-number-format'

import {
    useTheme,
    Paper,
    IconButton,
    Tooltip,
    Badge,
    Chip,
} from '@material-ui/core'
import FlagIcon from '@material-ui/icons/Flag'
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn'
import SignalWifiOff from '@material-ui/icons/SignalWifiOff'
import { Build, VideoLibrary } from '@material-ui/icons'

import {
    getAssignedUsersDisplay,
    UnitConfig,
    UnitInspection,
} from '../../models'
import { formatScheduleDateInZone } from '../../helpers'
import { useTimezone } from '../../contexts/timezone'
import { UnitInspectionStatusBadge } from './UnitInspectionStatusBadge'

import { ReactComponent as UnitNotesIcon } from '../../assets/icons/unitnotes.svg'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    unitInspection: UnitInspection
    unitConfig?: UnitConfig
    damageCount?: number
    unitNotesCount?: number
    unreadUnitNotes?: number
    selected?: boolean
    onClickCard?: () => void
    onClickDamage?: () => void
    onClickUnitNotes?: () => void
    onClickVideo?: () => void
}

const CARD_HEIGHT = 148

// Wrap with React.memo to prevent unnecessary re-renders when props haven't changed
export const UnitInspectionCard: React.FC<Props> = React.memo((props) => {
    const {
        unitInspection,
        unitConfig,
        damageCount = 0,
        unitNotesCount = 0,
        unreadUnitNotes = 0,
        selected,
        onClickCard,
        onClickDamage,
        onClickUnitNotes,
        onClickVideo,
    } = props

    const theme = useTheme()
    const { currentTimezone, propertyTimezone } = useTimezone()
    const displayZone = propertyTimezone ?? currentTimezone

    // Memoize totals to avoid recalculating on every render
    const { totalCost, totalFlags, totalHandled } = useMemo(() => {
        let cost = 0
        let flags = 0
        let handled = 0
        unitInspection.area_inspections.forEach((areaIns) => {
            cost += areaIns.cost
            flags += areaIns.flagged_count
            handled += areaIns.handled_count
        })
        return { totalCost: cost, totalFlags: flags, totalHandled: handled }
    }, [unitInspection.area_inspections])

    const folderPath = unitInspection.folder?.path ?? ''
    const folderName = unitInspection.folder?.name ?? ''
    const completedAreas =
        unitInspection.area_complete_ratio?.completed_areas ?? 0
    const totalAreas =
        unitInspection.area_complete_ratio?.total_areas ??
        unitInspection.area_inspections.length

    const displayUser = (unitInspection: UnitInspection) => {
        if (unitInspection.completed_by?.name) {
            return unitInspection.completed_by.name
        }
        return getAssignedUsersDisplay(
            unitInspection.assigned_users,
            unitInspection.assigned_to,
        )
    }

    return (
        <Paper
            style={{
                boxShadow: theme.shadows[2],
                backgroundColor: selected
                    ? theme.palette.primary.light
                    : theme.palette.background.paper,
                cursor: 'pointer',
                marginBottom: theme.spacing(1.5),
                borderRadius: theme.shape.borderRadius * 2,
                overflow: 'hidden',
                height: CARD_HEIGHT,
                minHeight: CARD_HEIGHT,
                flexShrink: 0,
                position: 'relative',
            }}
            onClick={onClickCard}
        >
            {unitInspection.offline_inspection ? (
                <Chip
                    icon={<SignalWifiOff style={{ fontSize: 14 }} />}
                    label="Offline"
                    size="small"
                    style={{
                        position: 'absolute',
                        top: theme.spacing(0.5),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1,
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        backgroundColor: theme.palette.grey[200],
                        marginTop: theme.spacing(1),
                    }}
                />
            ) : null}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: theme.spacing(1.5),
                    ...typographyStyle(theme.typography.body2),
                }}
            >
                {/* Top Row - Location & Unit info + Cost */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing(0.5),
                    }}
                >
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {/* Folder path */}
                        <span
                            style={{
                                flexDirection: 'row',
                                display: 'flex',
                                gap: theme.spacing(0.5),
                            }}
                        >
                            <span
                                style={{
                                    fontSize: '0.7rem',
                                    color: theme.palette.text.secondary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                }}
                            >
                                {folderPath}
                                {folderName}
                            </span>
                        </span>
                        {/* Unit name */}
                        <span
                            style={{
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {unitInspection.unit.name}
                        </span>
                        {/* Unit config */}
                        <span
                            style={{
                                fontSize: '0.75rem',
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {unitConfig?.name ?? ''}
                        </span>
                    </div>

                    {/* Right side - Status & Cost */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <UnitInspectionStatusBadge
                            unitInspection={unitInspection}
                            displayUser={displayUser(unitInspection)}
                        />
                        <span
                            style={{
                                fontSize: '0.7rem',
                                lineHeight: 1.1,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {completedAreas} of {totalAreas}
                        </span>
                        <NumberFormat
                            value={totalCost}
                            prefix="$"
                            thousandSeparator
                            displayType="text"
                            style={{
                                marginTop: theme.spacing(0.5),
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                lineHeight: 1,
                                color: theme.palette.text.primary,
                            }}
                        />
                    </div>
                </div>

                {/* Bottom Row - Assignment & Icons */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1.5),
                        flex: 1,
                    }}
                >
                    {/* Assignment info */}
                    <div
                        style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Tooltip title={displayUser(unitInspection)}>
                            <span
                                style={{
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {displayUser(unitInspection)}
                            </span>
                        </Tooltip>
                        <span
                            style={{
                                fontSize: '0.7rem',
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {formatScheduleDateInZone(
                                unitInspection.start_date,
                                displayZone,
                            )}{' '}
                            -{' '}
                            {formatScheduleDateInZone(
                                unitInspection.end_date,
                                displayZone,
                            )}
                        </span>
                    </div>

                    {/* Icons - flexShrink: 0 keeps them visible */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                            flexShrink: 0,
                        }}
                    >
                        <Badge badgeContent={totalFlags} color="secondary">
                            <FlagIcon
                                color={totalFlags > 0 ? undefined : 'disabled'}
                                fontSize="small"
                            />
                        </Badge>

                        {/* Handled */}
                        <Badge badgeContent={totalHandled} color="primary">
                            <AssignmentTurnedIn
                                color={
                                    totalHandled > 0 ? undefined : 'disabled'
                                }
                                fontSize="small"
                            />
                        </Badge>

                        {/* Videos */}
                        <Tooltip title="Videos">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onClickVideo?.()
                                }}
                                disabled={
                                    unitInspection.video_ids?.length === 0
                                }
                            >
                                <Badge
                                    badgeContent={
                                        unitInspection.video_ids?.length ?? 0
                                    }
                                    color="primary"
                                >
                                    <VideoLibrary fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        {/* Damages */}
                        <Tooltip title="Damages">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onClickDamage?.()
                                }}
                            >
                                <Badge
                                    badgeContent={damageCount}
                                    color="secondary"
                                >
                                    <Build fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        {/* Unit Notes */}
                        <Tooltip title="Unit Notes">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onClickUnitNotes?.()
                                }}
                            >
                                <Badge
                                    badgeContent={unitNotesCount}
                                    color={
                                        unreadUnitNotes > 0
                                            ? 'secondary'
                                            : 'primary'
                                    }
                                >
                                    <UnitNotesIcon
                                        style={{ width: 18, height: 18 }}
                                    />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </Paper>
    )
})
